import { useEffect, useRef, useState, startTransition } from 'react';
import { isEqual, pick } from 'lodash-es';
import { useStore } from '../store';

// 没有用 useSelector 而是手动 subscribe 监听 store，原因是 useSelector 是完全同步的，
// 当页面组件很多时（比如 ListElement 递归渲染大量 fields），每次 dispatch 后同步执行
// 大量 useSelector 会阻塞主线程，导致用户操作（如修改一个 Select 的值）要等好几秒才刷新。
//
// 这里用 setTimeout + startTransition 的组合实现异步低优先级更新：
// - setTimeout 跳出 React-Redux 的同步通知周期，让当前紧急渲染（如 Select 显示值更新）先完成并绘制
// - startTransition 告诉 React 这个更新是可中断的低优先级任务，React 会在空闲时间片里分批处理，
//   保持 UI 响应不卡顿
//
// 单独用 setTimeout 不够，因为 React 18 的自动批处理会把 setTimeout 里的 setState 当作紧急更新，
// 一旦开始渲染就不会中断。startTransition 解决的就是这个问题。
//
// TODO ids 其实应该叫 keyPathArr
export default function useDependencies(ids) {
  const store = useStore();
  const hasDeps = ids && ids.length > 0;

  // 稳定 ids 引用，避免内容相同但引用不同时触发不必要的更新
  const idsRef = useRef(ids);
  if (!isEqual(idsRef.current, ids)) {
    idsRef.current = ids;
  }
  const stableIds = idsRef.current;

  // 依赖值的快照，只通过 transition 异步更新
  const [depValues, setDepValues] = useState(() =>
    hasDeps ? pick(store.getState().formData.values, stableIds) : null,
  );

  useEffect(() => {
    if (!hasDeps) return undefined;

    let pending = false;

    const unsubscribe = store.subscribe(() => {
      // 合并：如果已经有一个宏任务在排队，跳过
      if (pending) return;
      pending = true;

      setTimeout(() => {
        pending = false;
        startTransition(() => {
          setDepValues((prev) => {
            const next = pick(store.getState().formData.values, stableIds);
            return isEqual(prev, next) ? prev : next;
          });
        });
      }, 0);
    });

    // effect 挂载时同步一次，防止 render 和 effect 之间 store 已经变了
    setDepValues((prev) => {
      const next = pick(store.getState().formData.values, stableIds);
      return isEqual(prev, next) ? prev : next;
    });

    return () => {
      unsubscribe();
    };
  }, [store, hasDeps, stableIds]);

  return depValues;
}
